import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage, FormattedNumber } from 'react-intl'
import { Box, Flex } from 'rebass/styled-components'
import AnimateHeight from 'react-animate-height'
import { scroller } from 'react-scroll'

import Total from './total'
import Checkbox from './checkbox'
import calculateTotal from './product/calculate-total'
import { BuyButton, UnstyledButton } from './button'
import OrderForm from './order-form'
import PaymentStatus from './payment-status'
import { createOrder } from '../api/order-create'
import { theme } from '../styles/theme'

const Order = ({
  onToggleOrder,
  onCountryChange,
  quantity,
  currency,
  productTitle,
  inStock,
  price,
  sku,
  accessories,
  chosenAccessories,
  complements,
  shippingPrice,
  freeShippingTotal,
  intl,
}) => {
  // This shows Order form
  const [order, setOrder] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  // Status of order used to show message to customer
  const [orderStatus, setOrderStatus] = useState({
    status: false,
    message: '',
  })

  // Hold chosen complements if present
  const [chosenComplements, setChosenComplements] = useState([])

  const toggleComplement = (id) => {
    setChosenComplements((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    )
  }

  // Build items list for total recalculation
  const itemsForTotal = [
    {
      title: productTitle,
      price,
      quantity,
      shippingPrice,
      accessories,
      chosenAccessories,
    },
  ]

  // Add selected complements as separate line items
  if (complements && chosenComplements.length > 0) {
    complements.forEach((c) => {
      if (chosenComplements.includes(c.id)) {
        itemsForTotal.push({ title: c.label, price: c.price, quantity: 1 })
      }
    })
  }

  const recalculated = calculateTotal(
    itemsForTotal,
    shippingPrice,
    freeShippingTotal,
  )

  const onOrderSubmit = (values) => {
    // Set submitting to true and send it to OrderForm
    setSubmitting(true)

    const orderComplements = (complements || [])
      .filter((c) => (chosenComplements || []).includes(c.id))
      .map((c) => ({ id: c.id, label: c.label, price: c.price, slug: c.slug }))

    createOrder({
      quantity,
      subtotal: Number(recalculated.subtotal).toFixed(2),
      total: Number(recalculated.total).toFixed(2),
      shipping: recalculated.shipping,
      legibleFreeShipping: recalculated.isFreeShipping,
      productTitle,
      sku,
      inStock,
      currency,
      orderComplements,
      chosenComplements,
      ...values,
    })
      .then((response) => {
        setSubmitting(false)

        if (response.status === 'success') {
          setOrder(false)
          setOrderStatus({
            status: 'success',
            message: intl.formatMessage({
              id: 'product.order.success',
              defaultMessage: 'Order successfully placed.',
            }),
          })
        } else {
          scroller.scrollTo('orderScroll', {
            duration: 500,
            offset: -16,
            smooth: 'easeInOutQuint',
          })

          setOrderStatus({
            status: 'error',
            message: response.message || 'Order failed. Please try again.',
          })
        }
      })
      .catch((error) => {
        setSubmitting(false)

        setOrderStatus({
          status: 'error',
          message: intl.formatMessage({
            id: 'product.order.failed',
            defaultMessage: error.message || 'Order failed. Please try again.',
          }),
        })
      })
  }

  return (
    <>
      {orderStatus.status && (
        <PaymentStatus status={orderStatus.status}>
          {orderStatus.message}
        </PaymentStatus>
      )}

      <AnimateHeight
        duration={400}
        easing={theme.easing}
        height={order ? 'auto' : 0}
      >
        {complements && complements.length > 0 && (
          <Box sx={{ p: 3, mb: 3, bg: '#fff', border: '1px solid #eee' }}>
            <Flex as="h3" sx={{ m: 0, mb: 2 }}>
              <FormattedMessage
                id="product.complements"
                defaultMessage="Complements"
              />
            </Flex>

            {complements.map((c) => {
              const isChosen = chosenComplements.includes(c.id)
              return (
                <Flex
                  key={c.id}
                  sx={{
                    alignItems: 'center',
                    py: 2,
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleComplement(c.id)}
                >
                  <Box sx={{ mr: 2 }}>
                    <Checkbox checked={isChosen} />
                  </Box>
                  <Box sx={{ flexGrow: 1, fontSize: 1, m: 0, p: 0 }}>
                    {c.label}
                  </Box>
                  <Box>
                    <FormattedNumber
                      style="currency"
                      value={c.price}
                      currency={currency}
                    />
                  </Box>
                </Flex>
              )
            })}
          </Box>
        )}

        {quantity > 0 && (
          <Box
            sx={{
              p: 3,
              mb: 3,
              bg: '#f7f4e4',
              border: '1px solid #eae2b8',
            }}
          >
            <Total
              quantity={Number(quantity)}
              subtotal={Number(recalculated.subtotal).toFixed(2)}
              total={Number(recalculated.total).toFixed(2)}
              currency={currency}
              unitPrice={Number(price)}
              productName={productTitle}
              shipping={recalculated.shipping}
              accessories={accessories}
              chosenAccessories={chosenAccessories}
              complements={complements}
              chosenComplements={chosenComplements}
            />
          </Box>
        )}

        <Box mb={3}>
          <OrderForm
            onSubmit={(values) => onOrderSubmit(values)}
            onCountryChange={(country) => onCountryChange(country)}
            isSubmitting={isSubmitting}
          />
        </Box>

        <Flex justifyContent="center">
          <UnstyledButton type="button" onClick={() => setOrder(false)}>
            <FormattedMessage
              id="product.order.cancelOrder"
              defaultMessage="Cancel order"
            />
          </UnstyledButton>
        </Flex>
      </AnimateHeight>

      <AnimateHeight
        duration={400}
        easing={theme.easing}
        height={!order ? 'auto' : 0}
        onAnimationEnd={onToggleOrder}
      >
        <BuyButton
          type="button"
          onClick={() => {
            setOrder(true)
            setOrderStatus({
              status: false,
              message: '',
            })
          }}
        >
          <FormattedMessage
            id="product.order.buttonLabel"
            defaultMessage="Order"
          />
        </BuyButton>
      </AnimateHeight>
    </>
  )
}

Order.propTypes = {
  inStock: PropTypes.bool,
  onToggleOrder: PropTypes.func,
  onCountryChange: PropTypes.func,
  quantity: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  productTitle: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  sku: PropTypes.string.isRequired,
  accessories: PropTypes.array,
  chosenAccessories: PropTypes.array,
  complements: PropTypes.array,
  shippingPrice: PropTypes.number,
  freeShippingTotal: PropTypes.number,
  intl: PropTypes.object.isRequired,
}

Order.defaultProps = {
  inStock: false,
  onToggleOrder: () => {},
  onCountryChange: () => {},
  accessories: null,
  chosenAccessories: null,
}

export default injectIntl(Order)
